/**
 * modes/animals-mode.js
 * Quản lý chế độ Động Vật 3D Voxel (Quadrupeds & Avians), đổi giống loài và các cử động sinh học.
 */

import { BlockyAnimal } from '../../avatars/blocky-animal.js';
import { ANIMAL_PRESETS, getAnimalSpeciesList } from '../../avatars/animal-presets.js';
import { ANIMAL_ACTIONS } from '../../avatars/animal-animator.js';
import { escapeHtml } from '../../core/html.js';

const ACTION_LABELS = {
  idle: '🧘 Đứng Yên (Idle)',
  walk: '🚶 Đi Bộ (Walk)',
  run: '🏃 Phi Nhanh (Run)',
  sit: '🧎 Ngồi (Sit)',
  sleep: '💤 Đi Ngủ (Sleep)',
  jump: '🦘 Bật Nhảy (Jump)',
  eat: '🌾 Gặm Cỏ (Eat)',
  peck: '🌾 Mổ Thóc (Peck)',
  flap: '🪽 Vỗ Cánh (Flap)'
};

export function setupAnimalsMode(state, helpers = {}) {
  const { clearCityScene, clearLabBench, clearAvatars, switchStudioPanel } = helpers;

  if (typeof clearCityScene === 'function') clearCityScene(state);
  if (typeof clearLabBench === 'function') clearLabBench(state);
  if (typeof clearAvatars === 'function') clearAvatars(state);

  if (state.studioEnvironmentGroup) state.studioEnvironmentGroup.visible = true;
  if (state.pedestalGroup) state.pedestalGroup.visible = true;
  if (state.gridHelper) state.gridHelper.visible = false;

  if (state.camera && state.controls) {
    state.camera.position.set(0, 1.4, 3.8);
    state.controls.target.set(0, 0.45, 0);
  }

  if (typeof switchStudioPanel === 'function') switchStudioPanel('animals');
  spawnAnimal(state.currentAnimalSpecies, state.currentAnimalVariant, state);
}

export function spawnAnimal(speciesKey, variantKey = null, state) {
  if (state.currentAnimal) {
    if (state.currentAnimal.root?.parent) {
      state.currentAnimal.root.parent.remove(state.currentAnimal.root);
    } else if (state.currentAnimal.root) {
      state.scene.remove(state.currentAnimal.root);
    }
    const idx = state.activeAvatars ? state.activeAvatars.indexOf(state.currentAnimal) : -1;
    if (idx !== -1) {
      state.activeAvatars.splice(idx, 1);
      state.currentAnimal.dispose();
    }
    state.currentAnimal = null;
  }

  state.currentAnimalSpecies = speciesKey;
  const preset = ANIMAL_PRESETS[speciesKey] || ANIMAL_PRESETS.DOG;
  state.currentAnimalVariant = variantKey || preset.defaultVariant;

  const animal = new BlockyAnimal(state.currentAnimalSpecies, state.currentAnimalVariant, {
    scale: state.currentAnimalScale || 1.0
  });
  animal.root.position.set(0, 0.08, 0);
  state.scene.add(animal.root);
  state.activeAvatars.push(animal);
  state.currentAnimal = animal;

  // Tự động điều chỉnh góc máy camera phù hợp với kích thước từng loài thú (P-1, P-2)
  if (state.camera && state.controls) {
    const finalScale = (preset.morph.scale || 1.0) * (state.currentAnimalScale || 1.0);
    const camDist = 2.4 + finalScale * 1.5;
    const camY = 0.5 + finalScale * 0.75;
    const targetY = finalScale * 0.35;
    state.camera.position.set(0, camY, camDist);
    state.controls.target.set(0, targetY, 0);
    state.controls.update();
  }

  animal.playAction(state.currentAnimalAnim || 'idle');

  updateAnimalPanelUI(state);
}

export function renderAnimalSpeciesGrid(state) {
  const grid = document.getElementById('animal-species-grid');
  if (!grid) return;
  grid.innerHTML = '';

  const speciesList = getAnimalSpeciesList();
  speciesList.forEach((sp) => {
    const card = document.createElement('div');
    card.className = `char-card ${sp.id === state.currentAnimalSpecies ? 'active' : ''}`;
    card.dataset.species = sp.id;
    card.innerHTML = `
      <div class="species-card-wrap">
        <div class="species-card__body">
          <span class="species-card__icon">${escapeHtml(sp.icon)}</span>
          <div>
            <div class="species-card__name">${escapeHtml(sp.name.split(' ')[0])}</div>
            <div class="species-card__sub">${sp.archetype === 'avian' ? 'Gia cầm' : '4 Chân'}</div>
          </div>
        </div>
        <span class="species-card__check">✓</span>
      </div>
    `;
    card.addEventListener('click', () => {
      document.querySelectorAll('#animal-species-grid .char-card').forEach((c) => c.classList.remove('active'));
      card.classList.add('active');
      spawnAnimal(sp.id, null, state);
    });
    grid.appendChild(card);
  });
}

export function updateAnimalPanelUI(state) {
  const preset = ANIMAL_PRESETS[state.currentAnimalSpecies];
  if (!preset) return;

  document.querySelectorAll('#animal-species-grid .char-card').forEach((c) => {
    c.classList.toggle('active', c.dataset.species === state.currentAnimalSpecies);
  });

  const badge = document.getElementById('current-animal-archetype-badge');
  if (badge) {
    badge.textContent = preset.archetype === 'avian' ? 'Gia Cầm' : '4 Chân';
    badge.style.background = preset.archetype === 'avian' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(56, 189, 248, 0.25)';
    badge.style.color = preset.archetype === 'avian' ? '#fbbf24' : '#7dd3fc';
  }

  const titleEl = document.getElementById('animal-desc-title');
  const descEl = document.getElementById('animal-desc-text');
  if (titleEl) titleEl.textContent = `${preset.icon} ${preset.name}`;
  if (descEl) descEl.textContent = preset.desc;

  const varGroup = document.getElementById('animal-variants-group');
  if (varGroup) {
    varGroup.innerHTML = '';
    Object.keys(preset.variants).forEach((vKey) => {
      const v = preset.variants[vKey];
      const btn = document.createElement('button');
      btn.className = `chip-btn ${vKey === state.currentAnimalVariant ? 'active' : ''}`;
      btn.dataset.variant = vKey;
      btn.innerHTML = `
        <span class="color-dot animal-variant-dot" style="--dot: ${v.baseColor};"></span>
        ${escapeHtml(v.name)}
      `;
      btn.addEventListener('click', () => {
        varGroup.querySelectorAll('.chip-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        state.currentAnimalVariant = vKey;
        if (state.currentAnimal) {
          state.currentAnimal.setVariant(vKey);
          state.currentAnimal.playAction(state.currentAnimalAnim || 'idle');
        }
      });
      varGroup.appendChild(btn);
    });
  }

  // Bind động các nút cử động từ ANIMAL_ACTIONS[archetype] (P-8)
  const actionsGroup = document.getElementById('animal-actions-group');
  if (actionsGroup) {
    actionsGroup.innerHTML = '';
    const actions = ANIMAL_ACTIONS[preset.archetype] || ANIMAL_ACTIONS.quadruped;
    const currentAction = state.currentAnimalAnim || 'idle';

    actions.forEach((act) => {
      const btn = document.createElement('button');
      btn.className = `chip-btn ${act === currentAction ? 'active' : ''}`;
      btn.dataset.action = act;

      // Giữ id duy nhất tương thích với test Playwright
      if (preset.archetype === 'avian') {
        if (act === 'peck') btn.id = 'btn-animal-eat';
        if (act === 'flap') btn.id = 'btn-animal-sit';
      } else {
        if (act === 'eat') btn.id = 'btn-animal-eat';
        if (act === 'sit') btn.id = 'btn-animal-sit';
      }

      btn.textContent = ACTION_LABELS[act] || act;
      btn.addEventListener('click', () => {
        actionsGroup.querySelectorAll('.chip-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        state.currentAnimalAnim = act;
        if (state.currentAnimal) {
          state.currentAnimal.playAction(act);
        }
      });
      actionsGroup.appendChild(btn);
    });
  }
}

export function initAnimalListeners(state) {
  renderAnimalSpeciesGrid(state);

  const slider = document.getElementById('slider-animal-scale');
  const label = document.getElementById('label-animal-scale');
  if (slider) {
    slider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      state.currentAnimalScale = val;
      if (label) label.textContent = `${val.toFixed(2)}x`;
      if (state.currentAnimal) {
        state.currentAnimal.setScale(val);
      }
    });
  }
}
